import isObject from "lodash/isObject";
import { BaseActivity } from "../../../model/activity/BaseActivity";
import { ActivityMetaMap } from "../../../model/activity/types/ActivityMetaKeysMap";
import { Text } from "../../Text";
import { ActivityAction } from "../../../model/activity/types/ActivityAction.enum";
import { t } from "i18next";
import isEmpty from "lodash/isEmpty";
import map from "lodash/map";
import isNil from "lodash/isNil";
import { ActivityEntityKind } from "../../../model/activity/types/ActivityEntityKind.enum";
import { ActivityMetaKeys } from "../../../model/activity/types/ActivityMetaKeys.enum";
import { EntryKind } from "../../../model/entry/types/EntryKind.enum";
import { GenericIcon } from "../../../model/shared/types/GenericIcon";
import { entrySliceSelectors } from "../../../redux/entry/entry.selector";
import { kanbanSliceSelectors } from "../../../redux/kanban/kanban.selector";
import { moduleSliceSelectors } from "../../../redux/module/module.selector";
import { permissionSliceSelectors } from "../../../redux/permission/permission.selector";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { getTranslatedKanbanColumnName } from "../../../routes/kanban/KanbanUtils";
import { standardDate, isValidDate } from "../../../utils/Date";
import { getFontAwesomeIcon } from "../../../utils/getFontAwesomeIcon";
import { AtiraIcon } from "../../AtiraIcon";
import isString from "lodash/isString";
import isArray from "lodash/isArray";
import isBoolean from "lodash/isBoolean";
import camelCase from "lodash/camelCase";
import { faCircle } from "@fortawesome/free-solid-svg-icons/faCircle";
import { RenderDiffPair } from "./RenderDiffPairs";
import {
  AUTOMATION_RULE_FIELD_LABEL_KEYS,
  getEntryActivityInput,
  isAutomationRuleActivity,
  isBlankDiff,
  isBlankValue,
  translateAutomationActionType,
  translateFieldLabel,
} from "../utils/activityUtils";
import styled from "styled-components";
import { Spacing } from "../../../theme/Spacing";
import { Flex } from "../../Flex";
import { getEntryLabel } from "../../../utils/entryUtil";
import { entryActions } from "../../../redux/entry/entry.slice";
import { AssigneeBanner } from "../../AssigneeBanner";
import { TagChip } from "../../TagChip";
import { useDrawersContext } from "../../../routes/tasks/drawers-context";

const DetailRow = styled(Flex)`
  flex-direction: column;

  &:not(:last-child) {
    border-bottom: ${({ theme }) => `1px solid ${theme.darkSub}`};
    padding-bottom: ${Spacing.m};
  }
`;

const IconWrapper = styled(Flex)`
  vertical-align: middle;
  margin-inline-end: ${Spacing.xs};
  display: inline-flex;
`;

const SuccessSpan = styled.span`
  color: ${({ theme }) => theme.success};
`;

const CircleIcon = <AtiraIcon icon={faCircle} color="main" width={5} />;

//

const isAssigneesCase = (subKey: string, subValue: any) =>
  subKey === "assigneeIds" && isArray(subValue) && subValue.every(isString);

const isMergeCase = (node: ActivityMetaMap[ActivityMetaKeys.MERGE]) =>
  node.mergedEntries?.length;

const isIconCase = (subKey: string, subValue: any) =>
  subKey === "icon" && subValue?.name && subValue?.prefix;

const isFormCase = (activity: BaseActivity) =>
  activity.kind === ActivityEntityKind.FORM;

const isModuleInstanceCase = (subKey: string, activity: BaseActivity) =>
  subKey === "moduleInstanceId" && isEmpty(activity.meta?.DETACH_MODULE);

const isModuleInstanceAttachmentFieldsCase = (subKey: string, subValue: any) =>
  subKey === "fields" && isObject(subValue);

/** Raw ids are noise - nothing a reader can act on, and never a name. */
const getHiddenDetailKeys = (activity: BaseActivity) => [
  "_id",
  "id",
  "kanbanCardId",
  ...(isEmpty(activity.meta?.DETACH_MODULE) ? [] : ["moduleInstanceId"]),
  // The moduleInstanceId row (isModuleInstanceCase below) already renders
  // this snapshot in full - iterating it again as its own top-level entry
  // would print every field a second time.
  ...(isEmpty(activity.meta?.ATTACH_MODULE) ? [] : ["fields"]),
];

type Props = {
  mapKey: keyof ActivityMetaMap;
  node: ActivityMetaMap[keyof ActivityMetaMap] | undefined;
  activity: BaseActivity;
};

export const RenderMetaNode: React.FC<Props> = ({ mapKey, node, activity }) => {
  const user = useAppSelector(userSliceSelectors.selectLoggedInUser);
  const tableSettings = useAppSelector(
    entrySliceSelectors.selectUserEntryTableSettings,
  );
  const members = useAppSelector(permissionSliceSelectors.selectMembers);

  const kanbansMeta = useAppSelector(kanbanSliceSelectors.selectKanbansMeta);
  const moduleDefinitions = useAppSelector(
    moduleSliceSelectors.selectAllModuleDefinitions,
  );
  const instances = useAppSelector(moduleSliceSelectors.selectInstances);
  const latestEntries = useAppSelector(entrySliceSelectors.selectLatestEntries);
  const dispatch = useAppDispatch();
  const { entry } = useDrawersContext();

  const getLabel = (activity: BaseActivity, subKey: string, node: any) => {
    const tableField = tableSettings?.columns?.find((c) => c.field === subKey);
    const entryInput = getEntryActivityInput(activity, entry, subKey);

    switch (true) {
      case !isNaN(Number(subKey)):
        return subKey;

      case isAutomationRuleActivity(activity) &&
        subKey in AUTOMATION_RULE_FIELD_LABEL_KEYS:
        return t(AUTOMATION_RULE_FIELD_LABEL_KEYS[subKey]);

      case activity.kind === ActivityEntityKind.ENTRY &&
        subKey === "moduleInstanceName":
        return t("common.moduleInstance");

      case activity.meta?.shared?.moduleKind === "MODULE_INSTANCE":
        switch (true) {
          case subKey === "entryId":
            return t("common.entry");
          case subKey === "parentId":
            return t("common.moduleDefinition");

          default:
            return (
              getModuleInstanceAttachmentFieldLabel(
                subKey,
                activity?.meta?.shared?.parentId,
              ) || translateFieldLabel(t, subKey)
            );
        }

      case !isEmpty(activity.meta?.ATTACH_MODULE) ||
        !isEmpty(activity.meta?.DETACH_MODULE):
        switch (true) {
          case subKey === "moduleInstanceName":
            return t("common.moduleInstance");
          case subKey === "parentId":
            return t("common.moduleDefinition");
          default:
            return translateFieldLabel(t, subKey);
        }

      case activity.kind === ActivityEntityKind.KANBAN_CARD:
        switch (true) {
          case subKey === "columnId" || subKey === "parentId":
            return t("common.column");
          case subKey === "entryId":
            return t("common.entry");
          case subKey === "title":
            return t("common.name");
          case subKey === "fromKanbanColumnId":
            return t("common.oldColumn");
          case subKey === "toKanbanColumnId":
            return t("common.newColumn");
          case subKey === "closedBy":
            return t("common.closedBy", {
              name:
                [...members, user!].find((m) => m._id === node.closedBy?.to)
                  ?.name || "-",
              date: standardDate(node.closedAt.to, user, true),
            });
          default:
            return translateFieldLabel(t, subKey);
        }

      case subKey === "assigneeIds":
        return t("common.assignees");

      case Boolean(entryInput?.label) &&
        (entryInput?.custom === true || subKey.startsWith("custom-field-")):
        return entryInput!.label;

      case subKey.startsWith("custom-field-") && !isEmpty(tableField):
        return tableField.label;

      case subKey === "parentId":
        return t(`common.parent`);

      default:
        return translateFieldLabel(t, subKey);
    }
  };

  const getColumnNameById = (id?: string) => {
    const kanban = kanbansMeta
      ?.flatMap((k) =>
        k?.columns?.map((c) => ({ column: c, template: k.template })),
      )
      ?.find((c) => c?.column?._id === id);

    return getTranslatedKanbanColumnName(
      kanban?.column?.name,
      kanban?.template,
    );
  };

  const getTaskParentName = (id?: string) => {
    const entry = latestEntries?.find((e) => e._id === id);

    if (entry) {
      return getEntryLabel(entry);
    }

    return "";
  };

  const getOwnerById = (id?: string) => {
    const owner = [...members, user].find((m) => m?._id === id);

    return owner?.name || t("common.user");
  };

  const getModuleInstanceById = (id?: string) => {
    // Legacy fallback ONLY, for ATTACH_MODULE activities logged before the
    // API started embedding a `fields` snapshot on the activity itself (see
    // isModuleInstanceCase below). An activity is a record of what happened
    // at a point in time - resolving the module instance's *current* fields
    // by id here turns that record into a live view that changes every time
    // someone edits the instance later, silently rewriting history (every
    // past activity referencing it converges on whatever the instance holds
    // right now). Do not extend this lookup to new call sites; store a
    // snapshot in the activity's meta instead.
    const moduleInstance =
      entry?.moduleInstances?.find((m) => m._id === id) ||
      entry?.moduleInstancesAttachments?.find((m) => m._id === id) ||
      instances?.find((m) => m._id === id);

    return moduleInstance?.fields;
  };

  const getModuleDefinitionNameById = (id?: string) => {
    const module = moduleDefinitions?.find((m) => m._id === id);

    return module?.name;
  };

  const getModuleInstanceAttachmentFieldLabel = (
    fieldKey: string,
    moduleDefinitionId?: string,
  ) => {
    const moduleDefinition = moduleDefinitions?.find(
      (m) => m._id === moduleDefinitionId,
    );

    return (
      moduleDefinition?.inputs?.find((f) => f.name === fieldKey)?.label ||
      t("common.uncategorized")
    );
  };

  const renderPrimitive = (v: any, key?: string, activity?: BaseActivity) => {
    if (isBlankValue(v)) {
      return "";
    }

    if (activity && isAutomationRuleActivity(activity)) {
      if (key === "actionType") {
        return translateAutomationActionType(t, v);
      }

      if (key === "column") {
        const kanban = kanbansMeta?.find(
          (k) => k._id === activity.meta?.shared?.parentId,
        );

        return getTranslatedKanbanColumnName(v, kanban?.template);
      }

      if (isArray(v) && v.every(isString)) {
        return v.join(", ");
      }
    }

    if (activity?.meta?.shared?.moduleKind === "MODULE_INSTANCE") {
      if (key === "kind") {
        return t(`modules.defintion.kind.${v.toLowerCase()}`);
      }

      if (key === "parentId") {
        return getModuleDefinitionNameById(activity?.meta?.shared?.parentId);
      }
    }

    if (
      !isEmpty(activity?.meta?.ATTACH_MODULE) ||
      !isEmpty(activity?.meta?.DETACH_MODULE)
    ) {
      if (key === "parentId" || key === "moduleId") {
        return getModuleDefinitionNameById(activity?.meta?.shared?.parentId);
      }
    }

    if (
      ["fromKanbanColumnId", "columnId", "toKanbanColumnId"].includes(
        key as string,
      )
    ) {
      return getColumnNameById(v);
    }

    if ("ownerId" === key) {
      return getOwnerById(v);
    }

    if (activity?.kind === ActivityEntityKind.TASK) {
      if ("parentId" === key) {
        return getTaskParentName(v);
      }

      if ("priority" === key && isString(v)) {
        return t(`common.${v.toLowerCase()}`, { defaultValue: v });
      }

      // The task's own *kind* (LEAD/CONTACT/KANBAN/ORPHAN) - not to be
      // confused with the entry kinds handled below, which happen to share
      // some of the same words.
      if ("kind" === key && isString(v)) {
        return t(`common.${camelCase(v)}_task`, { defaultValue: v });
      }
    }

    if (isBoolean(v)) {
      return v ? t("common.yes") : t("common.no");
    }

    if ("entryId" === key) {
      const entry = latestEntries?.find((e) => e._id === v);
      if (!entry) {
        dispatch(entryActions.getEntryById({ entryId: v, userId: user?._id! }));
      }
      return entry ? getEntryLabel(entry) : v;
    }

    if (Object.values(EntryKind).includes(v)) {
      return t(`common.${v.toLowerCase()}`);
    }

    if (isObject(v)) {
      try {
        return JSON.stringify(v);
      } catch {
        return String(v);
      }
    }

    if (isValidDate(v)) {
      return standardDate(v, user, true);
    }

    return String(v);
  };

  if (!node) {
    return <Text>{t("common.noDataToShow")}</Text>;
  }

  const hiddenKeys = getHiddenDetailKeys(activity);
  const renderableEntries = Object.entries(node).filter(
    ([subKey, subValue]) =>
      !hiddenKeys.includes(subKey) && !isBlankDiff(subValue),
  );

  if (!isObject(node)) {
    return renderPrimitive(node, mapKey, activity);
  }

  if (
    "from" in node ||
    "to" in node ||
    activity.action === ActivityAction.MOVE_BETWEEN_KANBANS
  ) {
    return <RenderDiffPair obj={{ [mapKey]: node }} activity={activity} />;
  }

  if (mapKey === ActivityMetaKeys.TAGS) {
    const { added, removed } = node as ActivityMetaMap[ActivityMetaKeys.TAGS];

    return (
      <Flex gap="m" flexDirection="column" width={"100%"}>
        {[
          { key: "added", tags: added },
          { key: "removed", tags: removed },
        ]
          .filter(({ tags }) => tags?.length)
          .map(({ key, tags }) => (
            <DetailRow key={key}>
              <Text>
                <IconWrapper>{CircleIcon}</IconWrapper>
                <strong>{t(`activity.tags.${key}`)}:</strong>
              </Text>
              <Flex gap="xs" flexWrap="wrap" marginInlineStart="m">
                {tags.map((tag) => (
                  <TagChip key={tag._id} tag={tag} />
                ))}
              </Flex>
            </DetailRow>
          ))}
      </Flex>
    );
  }

  if (isMergeCase(node as any)) {
    const _subValue = (node as ActivityMetaMap[ActivityMetaKeys.MERGE])
      .mergedEntries;

    return (
      <DetailRow>
        <Text>
          <IconWrapper>{CircleIcon}</IconWrapper>
          <strong>{t("activity.actions.merge-with")}:</strong>

          {_subValue?.map((entry) => (
            <Text key={entry._id} color="success">
              - {entry.title}
            </Text>
          ))}
        </Text>
      </DetailRow>
    );
  }

  return (
    <Flex gap="m" flexDirection="column" width={"100%"}>
      {renderableEntries.map(([subKey, subValue]) => {
        if (isModuleInstanceCase(subKey, activity)) {
          // Prefer the snapshot stored on the activity itself - only fall
          // back to a live lookup for activities logged before that
          // snapshot existed.
          const moduleInstanceFields =
            (node as ActivityMetaMap[ActivityMetaKeys.ATTACH_MODULE])?.fields ||
            getModuleInstanceById(subValue as string);
          return (
            <DetailRow key={subKey}>
              <Text>
                <IconWrapper>{CircleIcon}</IconWrapper>
                <strong>{t("common.moduleInstance")}:</strong>
                <SuccessSpan>
                  {map(moduleInstanceFields, (fieldValue, fieldKey) => (
                    <p key={fieldKey}>
                      {getModuleInstanceAttachmentFieldLabel(
                        String(fieldKey),
                        activity?.meta?.shared?.parentId,
                      )}
                      {" - "}
                      {String(fieldValue)}
                    </p>
                  ))}
                </SuccessSpan>
              </Text>
            </DetailRow>
          );
        }

        if (isModuleInstanceAttachmentFieldsCase(subKey, subValue)) {
          const isRuleSnapshot = isAutomationRuleActivity(activity);

          return Object.entries(subValue)
            .filter(
              ([key, value]) => value || (isRuleSnapshot && isBoolean(value)),
            )
            .map(([fieldKey, fieldValue]) => (
              <DetailRow key={fieldKey}>
                <Text>
                  <IconWrapper>{CircleIcon}</IconWrapper>
                  <strong>
                    {isRuleSnapshot
                      ? getLabel(activity, fieldKey, node)
                      : getModuleInstanceAttachmentFieldLabel(
                          fieldKey,
                          activity?.meta?.shared?.parentId,
                        )}
                  </strong>
                  <SuccessSpan>
                    {` ${renderPrimitive(fieldValue, fieldKey, activity)}`}
                  </SuccessSpan>
                </Text>
              </DetailRow>
            ));
        }

        if (isAssigneesCase(subKey, subValue)) {
          return (
            <DetailRow key={subKey}>
              <Text>
                <IconWrapper>{CircleIcon}</IconWrapper>
                <strong>{t("common.assignees")}:</strong>
              </Text>

              <Flex flexDirection="column" gap="xs" marginInlineStart="m">
                {(subValue as Array<string>)?.length ? (
                  (subValue as Array<string>).map((id, i) => {
                    const member = members.find((m) => m._id === id);
                    if (!member) {
                      return null;
                    }
                    return (
                      <Flex key={id} gap="xs" alignItems="center">
                        <Text>{member.name}</Text>
                        <AssigneeBanner hideSelf assignees={[member]} />
                      </Flex>
                    );
                  })
                ) : (
                  <Text color="danger">{t("common.none")}</Text>
                )}
              </Flex>
            </DetailRow>
          );
        }

        if (isIconCase(subKey, subValue)) {
          return (
            <DetailRow key={subKey}>
              <Text>
                <IconWrapper>{CircleIcon}</IconWrapper>
                <strong>{getLabel(activity, subKey, node)} </strong>
                <SuccessSpan>
                  {subValue ? (
                    <AtiraIcon
                      icon={getFontAwesomeIcon(subValue as GenericIcon)!}
                    />
                  ) : (
                    "-"
                  )}
                </SuccessSpan>
                .
              </Text>
            </DetailRow>
          );
        }

        if (isFormCase(activity)) {
          const omittedKeys = ["header", "footer", "leftSide", "style"];
          if (omittedKeys.includes(subKey)) {
            return null;
          }

          return (
            <DetailRow key={subKey}>
              <Text>
                <IconWrapper>{CircleIcon}</IconWrapper>
                <strong>{getLabel(activity, subKey, node)} </strong>
                <SuccessSpan>
                  {isObject(subValue) ? (
                    <RenderMetaNode
                      node={subValue}
                      activity={activity}
                      mapKey={subKey as keyof ActivityMetaMap}
                    />
                  ) : (
                    renderPrimitive(subValue, subKey, activity)
                  )}
                </SuccessSpan>
              </Text>
            </DetailRow>
          );
        }

        // lodash's `isEmpty` reports `false` and `0` as empty along with
        // `null`/`undefined`/`''` - that dropped a freshly-created task's
        // `done: false` and `contactAttempts: 0` from its own CREATE panel
        // outright, which is exactly the "details" a reader opens this for.
        if (!isBlankValue(subValue)) {
          return (
            <DetailRow key={subKey}>
              <Text>
                <IconWrapper>{CircleIcon}</IconWrapper>
                <strong>{getLabel(activity, subKey, node)} </strong>
                <SuccessSpan>
                  {isObject(subValue) &&
                  !(isAutomationRuleActivity(activity) && isArray(subValue)) ? (
                    <RenderMetaNode
                      node={subValue}
                      activity={activity}
                      mapKey={subKey as keyof ActivityMetaMap}
                    />
                  ) : (
                    renderPrimitive(subValue, subKey, activity)
                  )}
                </SuccessSpan>
              </Text>
            </DetailRow>
          );
        }

        return null;
      })}
    </Flex>
  );
};
